import { reactive, watch } from 'vue'

const STORAGE_KEY = 'mow_managers_signup'

export type FrequencyOption = {
    value: string
    label: string
}

export type SignupData = {
    address: string
    address_1: string
    city: string
    state: string
    zip: string
    lat: number | null
    lng: number | null
    market_id: string
    available_frequencies: FrequencyOption[]
    available_service_days: string[]
    day_pricing: Record<string, number>
    first_name: string
    last_name: string
    email: string
    phone: string
    frequency: string
    mow_areas: string
    corner_lot: string
    start_date: string
    notes: string
    optional_services: string[]
    lawn_sqft: number
    quote_price: number
    is_cellphone: string
    is_grass_six_inches: string
    bagged_grass_location: string
    is_grass_overgrown: string
    community_gate: string
    backyard_gate: string
    flower_beds: string
    pets_in_backyard: string
    city_pickup_requirement?: string
    white_vinyl_fence: string
    above_ground_pool: string
    trampoline: string
    gate_code: string
    backyard_gate_locked: string
    backyard_gate_code: string
    flower_beds_line: string
    pets_avoid: string
    vinyl_fence_damage: string
    pool_damage: string
    trampoline_damage: string
    special_requests: string
    referral: string
    referral_2: string
    billing_same_as_service: boolean
    billing_address: string
    billing_city: string
    billing_state: string
    billing_zip: string
    card_name: string
    card_number: string
    card_expiration: string
    card_cvv: string
    card_billing_zip: string
    melissa: Record<string, unknown>
    address_check: Record<string, unknown>
    analysis: { houseSqft: number; lawnSqft: number; mowableSqft: number } | null
    satellite_image: string
    polygons: Record<string, unknown> | null
    ai_available: boolean
    price: number
    tax: number
    tax_rate: number
    cost_service: number
    calculated_tax: number
    subtotal_mowing: number
    total_mowing: number
}

const defaults: SignupData = {
    address: '',
    address_1: '',
    city: '',
    state: '',
    zip: '',
    lat: null,
    lng: null,
    market_id: '',
    available_frequencies: [],
    available_service_days: [],
    day_pricing: {},
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    frequency: '',
    mow_areas: 'front_and_back',
    corner_lot: '',
    start_date: '',
    notes: '',
    optional_services: [],
    lawn_sqft: 0,
    quote_price: 0,
    is_cellphone: 'yes',
    is_grass_six_inches: '',
    bagged_grass_location: '',
    is_grass_overgrown: 'no',
    community_gate: 'no',
    backyard_gate: 'no',
    flower_beds: 'no',
    pets_in_backyard: 'no',
    city_pickup_requirement: '',
    white_vinyl_fence: 'no',
    above_ground_pool: 'no',
    trampoline: 'no',
    gate_code: '',
    backyard_gate_locked: 'no',
    backyard_gate_code: '',
    flower_beds_line: 'no',
    pets_avoid: 'no',
    vinyl_fence_damage: 'no',
    pool_damage: 'no',
    trampoline_damage: 'no',
    special_requests: '',
    referral: '',
    referral_2: '',
    billing_same_as_service: true,
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    card_name: '',
    card_number: '',
    card_expiration: '',
    card_cvv: '',
    card_billing_zip: '',
    melissa: {},
    address_check: {},
    analysis: null,
    satellite_image: '',
    polygons: null,
    ai_available: false,
    price: 0,
    tax: 0,
    tax_rate: 0,
    cost_service: 0,
    calculated_tax: 0,
    subtotal_mowing: 0,
    total_mowing: 0,
}

function load(): SignupData {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
            return { ...defaults, ...JSON.parse(raw) }
        }
    } catch {
        // corrupted data
    }
    return { ...defaults }
}

const state = reactive<SignupData>(load())

const SENSITIVE_FIELDS: (keyof SignupData)[] = ['card_number', 'card_cvv', 'card_expiration']

watch(state, (value) => {
    try {
        const filtered = { ...value }
        for (const field of SENSITIVE_FIELDS) {
            delete (filtered as Record<string, unknown>)[field]
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))
    } catch {
        // storage full or unavailable
    }
}, { deep: true })

export function useSignupStorage() {
    function clear(): void {
        Object.assign(state, defaults)
        try {
            localStorage.removeItem(STORAGE_KEY)
        } catch {
            // ignore
        }
    }

    return { signup: state, clearSignup: clear }
}
